// AdminHierarquia — gerencia relação superior↔subordinado entre usuários.
// Usado pelo escopo 'da equipe' em Atividades pra coordenadores verem
// atividades de subordinados.
//
// Acesso: só admin (validação no backend).
import { useEffect, useState } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Card, Select, Button, Tag, Typography, Space, Empty, Spin, Flex, Alert,
  App as AntApp, Form,
} from 'antd';
import {
  PlusOutlined, CloseOutlined, TeamOutlined, ArrowRightOutlined,
} from '@ant-design/icons';
import { API_URL } from '../config';

const { Title, Text } = Typography;

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

interface Vinculo {
  superior_email: string;
  subordinado_email: string;
  criado_em: string;
  criado_por: string;
}

interface Usuario { email: string; nome?: string; }

export function AdminHierarquiaInner() {
  const { message, modal } = AntApp.useApp();
  const [vinculos, setVinculos] = useState<Vinculo[]>([]);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [novoSup, setNovoSup] = useState<string | undefined>(undefined);
  // Multi-select agora: cada inclusão pode adicionar VÁRIOS subordinados de uma vez
  // ao mesmo superior. Backend aceita subordinado_emails: string[].
  const [novosSubs, setNovosSubs] = useState<string[]>([]);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => { carregar(); }, []);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const [r1, r2] = await Promise.all([
        axios.post(`${API_URL}/atividades`,
          { action: 'hierarquia_listar' },
          { headers: { Authorization: token } }),
        axios.get(`${API_URL}/admin/usuarios`,
          { headers: { Authorization: token } }).catch(() => ({ data: [] })),
      ]);
      const d1 = r1.data.body ? JSON.parse(r1.data.body) : r1.data;
      const d2 = r2.data.body ? JSON.parse(r2.data.body) : r2.data;
      setVinculos(Array.isArray(d1) ? d1 : []);
      setUsuarios(Array.isArray(d2) ? d2 : []);
    } catch (e: any) {
      console.error(e);
    } finally { setLoading(false); }
  }

  async function adicionar() {
    setErro('');
    if (!novoSup || novosSubs.length === 0) {
      setErro('Selecione superior e pelo menos 1 subordinado');
      return;
    }
    setSalvando(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/atividades`,
        { action: 'hierarquia_adicionar',
          superior_email: novoSup,
          subordinado_emails: novosSubs },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      const ok = d.adicionados || 0;
      const err = d.erros || 0;
      if (ok > 0 && err === 0) {
        message.success(`${ok} vínculo(s) adicionado(s).`);
      } else if (ok > 0 && err > 0) {
        const erros = (d.resultados || []).filter((x: any) => x.erro)
          .map((x: any) => `${x.email}: ${x.erro}`).join(' · ');
        message.warning(`${ok} adicionado(s), ${err} erro(s). ${erros}`);
      } else {
        const erros = (d.resultados || []).filter((x: any) => x.erro)
          .map((x: any) => `${x.email}: ${x.erro}`).join(' · ');
        setErro(erros || 'Nenhum vínculo adicionado');
        return;
      }
      setNovoSup(undefined); setNovosSubs([]);
      await carregar();
    } catch (e: any) {
      setErro(e.response?.data?.erro || e.message);
    } finally { setSalvando(false); }
  }

  function remover(v: Vinculo) {
    modal.confirm({
      title: 'Remover vínculo?',
      content: `${v.superior_email} → ${v.subordinado_email}`,
      okText: 'Remover',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        const token = await getToken();
        await axios.post(`${API_URL}/atividades`,
          { action: 'hierarquia_remover',
            superior_email: v.superior_email, subordinado_email: v.subordinado_email },
          { headers: { Authorization: token } });
        message.success('Vínculo removido.');
        await carregar();
      },
    });
  }

  const porSuperior: Record<string, Vinculo[]> = {};
  for (const v of vinculos) {
    if (!porSuperior[v.superior_email]) porSuperior[v.superior_email] = [];
    porSuperior[v.superior_email].push(v);
  }

  return (
    <div style={{ padding: '16px 24px', maxWidth: 1100, margin: '0 auto' }}>
      <Flex align="center" gap={14} style={{ marginBottom: 18 }} wrap>
        <Title level={4} style={{ margin: 0, color: '#020D7F' }}>
          <TeamOutlined /> Hierarquia de usuários
        </Title>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Define quem reporta a quem — usado pelo filtro "Da equipe" em Atividades.
          Cascata transitiva: superior vê atividades de subordinados E dos subordinados dos subordinados.
        </Text>
      </Flex>

      <Card
        size="small"
        title={<Text strong style={{ color: '#020D7F', fontSize: 12, letterSpacing: 0.4, textTransform: 'uppercase' }}>Novo vínculo</Text>}
        style={{ marginBottom: 18 }}
      >
        <Form layout="vertical" size="small">
          <Flex gap={10} align="end" wrap="wrap">
            <Form.Item label="Superior" style={{ flex: 1, minWidth: 200, marginBottom: 0 }}>
              <Select
                value={novoSup}
                onChange={(v) => { setNovoSup(v); setNovosSubs([]); }}
                placeholder="— escolha —"
                options={usuarios.map(u => ({ value: u.email, label: u.email }))}
                showSearch
              />
            </Form.Item>
            <div style={{ fontSize: 18, color: '#888', padding: '0 4px 4px' }}>
              <ArrowRightOutlined />
            </div>
            <Form.Item label="Subordinados (escolha 1+ pra incluir em lote)" style={{ flex: 2, minWidth: 280, marginBottom: 0 }}>
              <Select
                mode="multiple"
                value={novosSubs}
                onChange={setNovosSubs}
                placeholder={novoSup ? 'Selecione 1 ou mais usuários…' : 'Escolha o superior primeiro'}
                disabled={!novoSup}
                options={usuarios
                  .filter(u => u.email !== novoSup)
                  .map(u => ({ value: u.email, label: u.email }))}
                showSearch
                maxTagCount="responsive"
              />
            </Form.Item>
            <Button type="primary" icon={<PlusOutlined />} onClick={adicionar} loading={salvando}
              disabled={!novoSup || novosSubs.length === 0}>
              Adicionar ({novosSubs.length})
            </Button>
          </Flex>
        </Form>
        {erro && (
          <Alert
            type="error"
            showIcon
            message={erro}
            style={{ marginTop: 10 }}
            closable
            onClose={() => setErro('')}
          />
        )}
      </Card>

      {loading ? (
        <Flex justify="center" align="center" style={{ minHeight: 200 }}><Spin /></Flex>
      ) : Object.keys(porSuperior).length === 0 ? (
        <Card>
          <Empty
            description={
              <>
                <Title level={5} style={{ color: '#020D7F' }}>Nenhum vínculo cadastrado</Title>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Cadastre coordenadores → suas equipes para que eles vejam atividades dos subordinados.
                </Text>
              </>
            }
          />
        </Card>
      ) : (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          {Object.entries(porSuperior).map(([sup, subs]) => (
            <Card key={sup} size="small">
              <div style={{ marginBottom: 8 }}>
                <Text strong style={{ color: '#020D7F' }}>{sup}</Text>
                <Text type="secondary" style={{ fontSize: 10, marginLeft: 8 }}>
                  · {subs.length} subordinado(s)
                </Text>
              </div>
              <Space size={6} wrap>
                {subs.map(v => (
                  <Tag
                    key={v.subordinado_email}
                    closable
                    closeIcon={<CloseOutlined style={{ color: '#B91626' }} />}
                    onClose={(e) => { e.preventDefault(); remover(v); }}
                    color="processing"
                    style={{ margin: 0 }}
                  >
                    {v.subordinado_email}
                  </Tag>
                ))}
              </Space>
            </Card>
          ))}
        </Space>
      )}
    </div>
  );
}

export default function AdminHierarquia() {
  return (
    <AntApp>
      <AdminHierarquiaInner />
    </AntApp>
  );
}
